import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BookOpen, PlusCircle, Users, Edit, Trash2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function TeacherCourses({ user, onLogout }) {
  const [courses, setCourses] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCourse, setNewCourse] = useState({ title: '', description: '' });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/courses`, { headers: getAuthHeaders() });
      setCourses(res.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/courses`, newCourse, { headers: getAuthHeaders() });
      toast.success('Course created successfully!');
      setShowCreateDialog(false);
      setNewCourse({ title: '', description: '' });
      // Navigate directly to CourseBuilder with the new course ID
      navigate(`/courses/${res.data.id}/builder`);
    } catch (error) {
      console.error('Failed to create course:', error);
      toast.error('Failed to create course');
    }
  };

  const handleDeleteCourse = async (courseId, courseName) => {
    try {
      await axios.delete(`${API}/courses/${courseId}`, { headers: getAuthHeaders() });
      toast.success(`Course "${courseName}" deleted successfully!`);
      fetchCourses();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const handleForkCourse = async (courseId, courseName) => {
    try {
      const res = await axios.post(`${API}/courses/${courseId}/fork`, {}, { headers: getAuthHeaders() });
      toast.success(`Course "${courseName}" saved as "${res.data.title}"!`);
      fetchCourses();
    } catch (error) {
      toast.error('Failed to save course');
    }
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="courses"><div className="p-8">Loading...</div></Layout>;
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="courses">
      <div className="p-6 lg:p-8 space-y-6" data-testid="teacher-courses">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-navy">My Courses</h1>
            <p className="text-[#4D4D4D] mt-2 font-light">Create and manage your courses</p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-primary-blue hover:bg-[#1874CD] text-white flex items-center space-x-2 rounded-xl transition-smooth btn-press" data-testid="create-course-button">
                <PlusCircle className="w-4 h-4" strokeWidth={2} />
                <span>Create Course</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#E8F3FF] rounded-2xl shadow-2xl border-0">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-navy">Create New Course</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCourse} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-[15px] font-semibold text-navy">Course Title</Label>
                  <Input
                    id="title"
                    value={newCourse.title}
                    onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                    required
                    className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
                    data-testid="course-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-[15px] font-semibold text-navy">Description</Label>
                  <Textarea
                    id="description"
                    value={newCourse.description}
                    onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                    required
                    rows={4}
                    className="border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue transition-smooth bg-white"
                    data-testid="course-description-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl h-12 transition-smooth btn-press" data-testid="submit-course-button">
                  Create Course
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {courses.length === 0 ? (
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-16 text-center">
              <BookOpen className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
              <h3 className="text-xl font-bold text-navy mb-2">No Courses Yet</h3>
              <p className="text-[#4D4D4D] mb-6 font-light">Create your first course to get started</p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl transition-smooth btn-press"
              >
                Create Course
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => {
              const getThumbnailUrl = () => {
                if (!course.thumbnail_url) return null;
                if (course.thumbnail_url.startsWith('http')) return course.thumbnail_url;
                return `${BACKEND_URL}${course.thumbnail_url}`;
              };

              return (
                <Card key={course.id} className="border-0 card-shadow bg-white rounded-2xl overflow-hidden card-hover transition-smooth" data-testid={`course-card-${course.id}`}>
                  {/* Course Thumbnail */}
                  {course.thumbnail_url && (
                    <div className="relative h-40 overflow-hidden">
                      <img src={getThumbnailUrl()} alt={course.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-3 left-4 right-4">
                        <h3 className="text-lg font-bold text-white line-clamp-2">{course.title}</h3>
                      </div>
                    </div>
                  )}

                  <CardContent className="p-5 space-y-4">
                    {/* Title if no thumbnail */}
                    {!course.thumbnail_url && (
                      <CardHeader className="p-0">
                        <CardTitle className="text-xl font-bold text-navy">{course.title}</CardTitle>
                      </CardHeader>
                    )}

                    <p className="text-sm text-[#4D4D4D] line-clamp-2 font-light">{course.description}</p>
                    <div className="flex items-center text-sm text-[#4D4D4D]">
                      <Users className="w-4 h-4 mr-2" strokeWidth={2} />
                      <span>{course.students_assigned?.length || 0} students enrolled</span>
                    </div>
                    <div className="space-y-2">
                      <Button
                        onClick={() => navigate(`/courses/${course.id}/builder`)}
                        className="w-full bg-primary-blue hover:bg-[#1874CD] text-white flex items-center justify-center space-x-2 rounded-xl transition-smooth btn-press"
                        data-testid={`edit-course-button-${course.id}`}
                      >
                        <Edit className="w-4 h-4" strokeWidth={2} />
                        <span>Edit Course</span>
                      </Button>
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => handleForkCourse(course.id, course.title)}
                          variant="outline"
                          className="flex-1 border-2 border-[#E7E7E7] text-primary-blue flex items-center justify-center space-x-2 rounded-xl hover:border-primary-blue transition-smooth"
                          data-testid={`fork-course-button-${course.id}`}
                        >
                          <Copy className="w-4 h-4" strokeWidth={2} />
                          <span>Copy</span>
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="destructive"
                              className="flex-1 rounded-xl"
                              data-testid={`delete-course-button-${course.id}`}
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={2} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-2xl">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-xl font-bold text-navy">Delete Course?</AlertDialogTitle>
                              <AlertDialogDescription className="text-[#4D4D4D]">
                                Are you sure you want to delete "{course.title}"? This will permanently delete all modules, lessons, quizzes, and student progress. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteCourse(course.id, course.title)}
                                className="bg-red-600 hover:bg-red-700 rounded-xl"
                              >
                                Delete Course
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
